import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import { observer } from 'mobx-react';
import {
  ItemDisplay,
  LeverageCard,
  LeverageCardRow,
  PercentagePicker,
} from '../fixFlip/leverageComponents';
import { LeverageCardProps } from '../fixFlip/leverageStep';
import { CurrencyField, FieldLabel } from '@roc/ui';
import { makeStyles } from '@material-ui/core/styles';
import { isNotBlank } from '@roc/feature-utils';
import { OneToolCardBorder } from '../fixFlip/oneToolCard';
import { Box, Checkbox } from '@material-ui/core';
import { LeveragePricingType } from '../../utils/constants';
import clsx from 'clsx';

const fieldLabelStyle = {
  fontSize: '14px',
  textAlign: 'center',
  width: '100%',
  display: 'block',
  color: '#939494',
  marginBottom: '0px',
  marginTop: '4px',
};

const useStyles = makeStyles({
  fieldValue: {
    '& .MuiInput-input': {
      color: 'rgb(107, 108, 113)',
      fontSize: '20px',
      textAlign: 'center',
      fontWeight: 'bold',
    },
  },
  checkbox: {
    marginRight: '16px',
  },
  chooseCard: {
    margin: '4px 16px',
    marginLeft: '32px',
    width: '100%',
    height: '130px',
    cursor: 'default',
    display: 'flex',
    alignItems: 'center',
  },
  chooseCardSelected: {
    background: '#EEF7FC',
    '&:hover': {
      background: '#EEF7FC ',
    },
  },
});


export const StabilizedBridgeLeverageCard = observer(
  ({ store, disabled, quoteTypeStore }: LeverageCardProps) => {

    const calculated = quoteTypeStore.loanEconomicsStore.calculateFields();

    const showEmptyValues = false;

    //Do not remove: observe changes to these fields to update calculations and displayed values on the card
    const initialAdvance = store.form.fields.initialAdvance.value;


    const handleInitialAdvance = value => {
      store.onFieldChange('initialAdvance', value);
    };

    const handleCheckboxChange = (value) => {
      store.onFieldChange('pricingType', value);
    };


    return (
      <LeverageCard
        title={'Choose Leverage'}
        description={'Select Loan to Cost (LTC) or Loan to Value (LTV) for pricing.'} disabled={disabled}
      >
        <SelectableLeverageCard
          checked={store.form.fields.pricingType.value === LeveragePricingType.LTC}
          onChange={() => handleCheckboxChange(LeveragePricingType.LTC)}
          leverageCardRow={
            <LeverageCardRow
              dividend={
                <LoanAmount store={store} onChange={handleInitialAdvance} />
              }
              divisor={
                <ItemDisplay
                  label={
                    quoteTypeStore.isMidConstructionRefinance
                      ? 'Cost Basis'
                      : 'Purchase Price'
                  }
                  value={
                    showEmptyValues
                      ? '-'
                      : formatCurrency(calculated.totalPurchasePrice)
                  }
                />
              }
              result={
                <ItemDisplay
                  label="Loan to Cost"
                  value={showEmptyValues ? '-' : formatPercentage(calculated.loanToCost)}
                />
              }
            />
          }
        />
        <SelectableLeverageCard
          checked={store.form.fields.pricingType.value === LeveragePricingType.LTV}
          onChange={() => handleCheckboxChange(LeveragePricingType.LTV)}
          leverageCardRow={
            <LeverageCardRow
              dividend={
                <ItemDisplay
                  label="Total Loan Amount"
                  value={
                    showEmptyValues
                      ? '-'
                      : formatCurrency(calculated.totalLoanAmount)
                  }
                />
              }
              divisor={
                <ItemDisplay
                  label="Market Value"
                  value={
                    showEmptyValues
                      ? '-'
                      : formatCurrency(calculated.totalAsIsValue)
                  }
                />
              }
              result={
                <ItemDisplay
                  label="Loan to Value"
                  value={showEmptyValues ? '-' : formatPercentage(calculated.loanToValue)}
                />
              }
            />
          }
        />
      </LeverageCard >
    );
  }


);


export const LoanAmount = observer(({ store, onChange, ...rest }) => {
  const formFields = store.form.fields;
  const classes = useStyles();
  return (
    <>
      <FieldLabel style={fieldLabelStyle}>Total Loan Amount</FieldLabel>
      <CurrencyField
        inputClassname={classes.fieldValue}
        testId="loanAmount"
        value={formFields.initialAdvance.value}
        onChange={(e, value) => onChange(value)}
        error={isNotBlank(formFields.initialAdvance.error)}
      />
    </>
  );
});

export const SelectableLeverageCard = observer(({ onChange, checked, leverageCardRow }) => {
  const classes = useStyles();
  const cardClass = clsx(classes.chooseCard, {
    [classes.chooseCardSelected]: checked,
  });
  return (
    <OneToolCardBorder className={cardClass}>
      <Box display="flex" alignItems="center" flex={1}>
        <Checkbox
          className={classes.checkbox}
          checked={checked}
          onChange={onChange}
          color="primary"
        />
        <Box flex={1}>
          {leverageCardRow}
        </Box>
      </Box>
    </OneToolCardBorder>
  );
});
